import { useCallback, useEffect, useMemo, useState } from "react"
import { BubbleMenu } from "@tiptap/react/menus"
import { useEditorState, type Editor } from "@tiptap/react"
import { Volume2, Loader2, BookOpen } from "lucide-react"
import { toast } from "sonner"
import { useTranslation } from "react-i18next"
import { useQueryClient } from "@tanstack/react-query"
import { Button } from "@/components/ui/button"
import { VocabularyCaptureOutcomeEnum, VocabularyEntryStateEnum } from "@/__generated__/graphql"
import { useAuth } from "@/lib/auth"
import { captureErrorKey } from "@/lib/flashcards"
import { invalidateAfterCapture, type VocabularyEntryUpdate } from "@/lib/vocabulary-entries"
import {
  MAX_FLASHCARD_TEXT_LENGTH,
  captureLessonFlashcard,
  removeFlashcardCapture,
} from "@/lib/live/flashcard-capture"
import { captureContextFor } from "@/lib/live/capture-context"
import {
  lookupWord,
  readingFor,
  speakWord,
  speechSupported,
  type WordDefinition,
} from "@/lib/live/word-lookup"
import { isSignedLanguage } from "@/lib/signedLanguages"
import { applyCaptureMark, trackCaptureRange } from "./capture-mark"

interface WordPopoverProps {
  editor: Editor | null
  lessonId: string
  targetLanguageCode?: string
  // Every entry the server returns (fresh or reused, queued or already saved)
  // goes here so the "Vocabulary this lesson" strip lists it at once.
  onCaptured?: (entry: VocabularyEntryUpdate) => void
  // The toast's Undo; the classroom passes the capture hook's undo, which also
  // removes the marker. Without it the popover cancels directly.
  onUndo?: (entryId: string) => void
}

// Longest selection we'll run a dictionary lookup for. A phrase of two or
// three words is still a flashcard (the server takes up to
// MAX_FLASHCARD_TEXT_LENGTH), it just gets no definition box.
const LOOKUP_MAX = 40

// The popover shows for any selection a flashcard can be made from; longer
// than that is a sentence, not a term, and gets nothing. Exported for tests —
// BubbleMenu's shouldShow is not reachable through the rendered DOM.
export function popoverShouldShow(text: string): boolean {
  return text.length > 0 && text.length <= MAX_FLASHCARD_TEXT_LENGTH
}

// Long enough to read and hit Undo, well inside the server's 60-second
// cancellation window (the chat VocabularyPopup uses the same).
const UNDO_TOAST_MS = 8000

type DefState = { loading: boolean; value: WordDefinition | null }

// The card always lands in the STUDENT's deck, so "own language" means the
// student's L1 whoever captured: the student reads "your own language", the
// tutor "your student's own language". Anyone else (staff) gets the neutral line.
export function skippedNativeLanguageKey(role: string | undefined): string {
  if (role === "student") return "liveSession.wordPopover.toast.skippedNativeLanguageStudent"
  if (role === "tutor") return "liveSession.wordPopover.toast.skippedNativeLanguageTutor"
  return "liveSession.wordPopover.toast.skippedNativeLanguage"
}

// #878: the same collision, but the L1 was only guessed from the interface
// locale. Mid-lesson is the wrong moment for a modal, so the classroom only
// tells them — the question itself is offered on the calm surfaces (the chat
// popup and the New deck dialog). The student's line points at the fix; the
// tutor's does not, because they cannot answer for their student.
export function needsNativeLanguageKey(role: string | undefined): string {
  if (role === "student") return "liveSession.wordPopover.toast.needsNativeLanguageStudent"
  if (role === "tutor") return "liveSession.wordPopover.toast.needsNativeLanguageTutor"
  return "liveSession.wordPopover.toast.needsNativeLanguage"
}

// The word-lookup popover: a floating card anchored to a selection in the lesson
// notes. Surfaces a contextual reading (pinyin, client-side), a quick definition
// (server lookup — cache / existing flashcard / dictionary, no AI), a pronounce
// button (browser speech), and the "make a flashcard" action. Lookup and
// flashcard generation stay separate: this is the instant read-path; the flashcard
// button kicks off the heavier async pipeline. A fresh capture marks the selected
// text (the marker replicates to the peer) and the strip lists it.
export function WordPopover({ editor, lessonId, targetLanguageCode, onCaptured, onUndo }: WordPopoverProps) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const qc = useQueryClient()
  const role = user?.role
  const selection = useEditorState({
    editor,
    selector: ({ editor }) => {
      if (!editor) return ""
      const { from, to, empty } = editor.state.selection
      return empty ? "" : editor.state.doc.textBetween(from, to, " ").trim()
    },
  }) ?? ""

  const reading = useMemo(() => readingFor(selection, targetLanguageCode), [selection, targetLanguageCode])
  const [def, setDef] = useState<DefState>({ loading: false, value: null })
  const [saving, setSaving] = useState(false)
  const canSpeak = speechSupported()
  const tooLongToCapture = selection.length > MAX_FLASHCARD_TEXT_LENGTH

  // Debounced server lookup, re-run as the selection changes. A guard flag drops
  // stale responses so a slow lookup can't overwrite a newer selection's result.
  useEffect(() => {
    const text = selection
    if (!text || text.length > LOOKUP_MAX) {
      setDef({ loading: false, value: null })
      return
    }
    let active = true
    setDef({ loading: true, value: null })
    const timer = setTimeout(() => {
      lookupWord(lessonId, text)
        .then((value) => active && setDef({ loading: false, value }))
        .catch(() => active && setDef({ loading: false, value: null }))
    }, 250)
    return () => {
      active = false
      clearTimeout(timer)
    }
  }, [selection, lessonId])

  const handleCapture = useCallback(async () => {
    if (!editor || !selection || tooLongToCapture || saving) return
    setSaving(true)
    // The range to mark, ANCHORED before the await. Raw numbers would be stale
    // by the time the mutation answers: a peer editing the notes in that window
    // shifts the whole document, and the marker would land on the wrong words
    // (forever — syncCaptureMarks reconciles state, never position).
    const { from, to } = editor.state.selection
    const tracked = trackCaptureRange(editor, { from, to })
    try {
      const result = await captureLessonFlashcard(lessonId, selection, captureContextFor(editor))
      if (result.errors.length > 0) {
        // A stable code has translated copy; an unknown code (older server,
        // new refusal) still shows the server's own sentence.
        const key = captureErrorKey(result.errorCode)
        toast.error(key ? t(key) : result.errors[0])
        return
      }
      const entry = result.vocabularyEntry
      // Only an entry THIS capture created goes to the strip (and gets marked
      // and gets Undo) — a reused one may be the other participant's, or this
      // owner's own earlier capture, and the shared log carries no "reused"
      // flag: listing it here would let Undo call RemoveVocabularyHighlight on
      // that older entry and destroy its already-materialized flashcards. A
      // reused entry keeps its Phase 1 toast (queued / already-saved) and
      // nothing else — it is never undoable, never marked, never listed.
      const fresh = entry && !result.reused ? entry : null
      if (fresh) onCaptured?.(fresh)
      switch (result.outcome) {
        case VocabularyCaptureOutcomeEnum.Queued: {
          // Outside the try below on purpose: the capture has already succeeded
          // (it is in the strip and a card is generating), so a marking problem
          // must never be reported to the student as a failed capture.
          if (fresh) {
            try {
              applyCaptureMark(editor, tracked.current(), { entryId: fresh.id, state: VocabularyEntryStateEnum.Pending })
            } catch {
              /* the underline is missing; the capture stands */
            }
          }
          // The card lands in the STUDENT's deck: the tutor is told whose it is.
          toast.success(t(role === "tutor" ? "liveSession.wordPopover.toast.queuedTutor" : "liveSession.wordPopover.toast.queued", { word: selection }), {
            description: t("liveSession.wordPopover.toast.queuedDescription"),
            duration: UNDO_TOAST_MS,
            action: fresh
              ? {
                  label: t("liveSession.wordPopover.toast.undo"),
                  onClick: () => {
                    if (onUndo) {
                      onUndo(fresh.id)
                    } else {
                      removeFlashcardCapture(fresh.id)
                        .then((res) => res.success && invalidateAfterCapture(qc))
                        .catch(() => {})
                    }
                  },
                }
              : undefined,
          })
          invalidateAfterCapture(qc)
          break
        }
        case VocabularyCaptureOutcomeEnum.AlreadySaved:
          toast.success(t("liveSession.wordPopover.toast.alreadySaved", { word: selection }))
          invalidateAfterCapture(qc)
          break
        case VocabularyCaptureOutcomeEnum.SkippedNativeLanguage:
          toast.info(t(skippedNativeLanguageKey(role)))
          break
        case VocabularyCaptureOutcomeEnum.NeedsNativeLanguage:
          toast.info(t(needsNativeLanguageKey(role)))
          break
        case VocabularyCaptureOutcomeEnum.UnsupportedLanguage:
          toast.info(t("liveSession.wordPopover.toast.unsupportedLanguage"))
          break
        default:
          toast.error(t("liveSession.wordPopover.toast.failed"))
      }
    } catch {
      toast.error(t("liveSession.wordPopover.toast.failed"))
    } finally {
      tracked.stop()
      setSaving(false)
    }
  }, [editor, lessonId, selection, tooLongToCapture, saving, role, t, qc, onCaptured, onUndo])

  // For a signed target language every affordance here is speech/text-centric
  // (pinyin reading, dictionary, pronounce, text flashcard — capture is also
  // refused server-side), so don't offer the popover at all.
  if (!editor || isSignedLanguage(targetLanguageCode)) return null

  return (
    <BubbleMenu
      editor={editor}
      options={{ placement: "bottom", offset: 8 }}
      shouldShow={({ editor, from, to }) => {
        if (!editor || from === to) return false
        return popoverShouldShow(editor.state.doc.textBetween(from, to, " ").trim())
      }}
      // Keep the editor selection alive when interacting with the card — a blur
      // would collapse the selection before the command runs.
      onMouseDown={(e) => e.preventDefault()}
      className="w-72 max-w-[80vw] rounded-lg border border-border bg-popover text-popover-foreground shadow-md"
    >
      <div className="p-3 space-y-2">
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <div className="font-medium leading-tight break-words">{selection}</div>
            {reading && <div className="text-sm text-muted-foreground">{reading}</div>}
          </div>
          {canSpeak && (
            <Button
              variant="ghost"
              size="icon"
              className="shrink-0"
              aria-label={t("liveSession.wordPopover.pronounce")}
              onClick={() => speakWord(selection, targetLanguageCode)}
            >
              <Volume2 className="w-4 h-4" />
            </Button>
          )}
        </div>

        {/* Phrases past LOOKUP_MAX get no dictionary row at all — "No
            definition found." would read as a lookup that failed. */}
        {selection.length <= LOOKUP_MAX && (
        <div className="text-sm min-h-5">
          {def.loading ? (
            <span className="inline-flex items-center gap-1.5 text-muted-foreground">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> {t("liveSession.wordPopover.lookingUp")}
            </span>
          ) : def.value?.definition ? (
            <span className="text-foreground break-words">{def.value.definition}</span>
          ) : (
            <span className="text-muted-foreground">{t("liveSession.wordPopover.noDefinition")}</span>
          )}
        </div>
        )}

        <Button
          size="sm"
          className="w-full"
          onClick={handleCapture}
          disabled={saving || tooLongToCapture}
          aria-busy={saving}
          aria-label={saving ? t("liveSession.wordPopover.createFlashcard") : undefined}
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
          ) : (
            <>
              <BookOpen className="w-4 h-4 mr-1.5" />
              {tooLongToCapture ? t("liveSession.wordPopover.selectionTooLong") : t("liveSession.wordPopover.createFlashcard")}
            </>
          )}
        </Button>
      </div>
    </BubbleMenu>
  )
}
